import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step2 from "@/components/menu-management/special-menu/bunquet-night/add-banquet-night/step2";
import Step1 from "@/components/menu-management/special-menu//bunquet-night/add-banquet-night/step1";
import Step3 from "@/components/menu-management/special-menu/bunquet-night/add-banquet-night/step3";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewBanquet,
  clearBanquetMessages,
  updateExistingBanquet,
} from "@/redux/slice/menu-manage-slice/special-menu/banquet-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { fetchAllDays } from "@/redux/slice/utility/day-slice";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const menu_id = 16;

const normalizeDayIds = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((dt) => (typeof dt === "object" && dt !== null ? dt.day_id ?? dt.id : dt))
    .filter((id) => id !== null && id !== undefined && !isNaN(Number(id)))
    .map(Number);
};

function CreateBanquetNight() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;
  const [dayTimes, setDayTimes] = useState([]);

  const { createLoading, updateLoading } = useSelector(
    (state) => state.menuManage.banquet
  );
  const { getLoading } = useSelector((state) => state.menuManage.menu);
  const [extraItems, setExtraItems] = useState([]);
  const { getLoading: daysLoading } = useSelector((state) => state.utility);

  const form = useForm({
    resolver: zodResolver(createBanquetNight),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: null,
      name: "",
      description: "",
      person_limit: null,
      days: [],
      courses: [],
      extra_items: [],
      price: 0,
      vat: 0,
      status: "active",
      file_manager_id: undefined,
    },
  });

  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    dispatch(fetchAllFlatMenu(user.restaurant_id));
    dispatch(fetchAllDays());
  }, []);

  useEffect(() => {
    const dayIds = normalizeDayIds(dayTimes);
    form.setValue("days", dayIds);
  }, [dayTimes]);

  useEffect(() => {
    form.setValue("extra_items", extraItems);
  }, [extraItems]);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/special-menu/banquet-night");
      return;
    }
    const initialDays = normalizeDayIds(item?.days);
    form.reset({
      type: "update",
      item_no: item?.item_no || null,
      name: item?.name || "",
      description: item?.description || "",
      price: item?.price || null,
      person_limit: item?.person_limit,
      vat: item?.vat ?? 0,
      status: item?.status || "active",
      file_manager_id: item?.file_manager_id || undefined,
      days: initialDays,
      courses: item?.courses || [],
      extra_items: item?.extra_items || [],
    });

    if (item?.file_manager_id && item?.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    setDayTimes(initialDays);
    dispatch(setItemVariants(item?.courses));
    setExtraItems(item?.extra_items || []);
  }, [type, item?.id, form]);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Banquet night",
      user,
      createAction: createNewBanquet,
      updateAction: updateExistingBanquet,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/special-menu/banquet-night",
    });

  if (getLoading || daysLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type == "update" ? "Update Banquet Night" : "Add New Banquet Night"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        {
          name: "Special Menu",
          path: "/backoffice/menu/special-menu/dish-the-day",
        },
        {
          name: "Banquet Night",
          path: "/backoffice/menu/special-menu/banquet-night",
        },
        {
          name: `${
            type == "update" ? "Update Banquet Night" : "Add New Banquet Night"
          }`,
          path: `/backoffice/menu/special-menu/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form>
            {step === 0 && (
              <Step1
                dayTimes={dayTimes}
                setDayTimes={setDayTimes}
                menu_id={menu_id}
                form={form}
              />
            )}
            {step === 1 && (
              <Step2
                extraItems={extraItems}
                setExtraItems={setExtraItems}
                menu_id={menu_id}
                item_id={entityId || item?.id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/special-menu/banquet-night"
              onBack={prevStep}
              onNext={async () => {
                const dayIds = normalizeDayIds(dayTimes);
                const formData = {
                  ...form.getValues(),
                  days: dayIds,
                };
                await nextStep("draft", formData, form);
              }}
              onSubmit={async () => {
                const data = form.getValues();
                const dayIds = normalizeDayIds(dayTimes);
                await nextStep(data?.status, { ...data, days: dayIds }, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                const dayIds = normalizeDayIds(dayTimes);
                await nextStep("draft", { ...data, days: dayIds }, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateBanquetNight;

export const createBanquetNight = z.object({
  item_no: z
    .preprocess(
      (val) => (val === "" || val == null ? null : Number(val)),
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),

  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),

  // days: z
  //   .array(z.number(), {
  //     required_error: "At least one day is required",
  //   })
  //   .optional()
  //   .nullable(),

  person_limit: z
    .preprocess(
      (val) => (val === "" || val == null ? null : Number(val)),
      z
        .number({
          invalid_type_error: "Person limit must be a number",
        })
        .nullable()
    )
    .optional(),

  courses: z.array(z.object({}).catchall(z.any())).optional(),

  extra_items: z.array(z.record(z.any())).optional(),

  price: z.preprocess(
    (val) => (val === "" || val == null ? undefined : Number(val)),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),

  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT must be a number",
      })
    )
    .refine((val) => val >= 0, { message: "VAT cannot be negative" })
    .refine((val) => val <= 100, { message: "VAT cannot exceed 100" })
    .optional(),

  status: z.enum(["active", "inactive", "draft"]).default("active"),

  file_manager_id: z
    .preprocess(
      (val) => (val === "" || val == null ? undefined : Number(val)),
      z
        .number({
          required_error: "Please upload an image",
          invalid_type_error: "File ID must be a number",
        })
        .min(1, "File ID must be a positive integer")
    )
    .optional(),
});
